import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import ThesItemPayamLib from "./ThesItemPayamLib";
import FilterFormPayamLib from "./FilterFormPayamLib";
import { useThesis } from "hooks/useThesis";
import { Loading } from "components";

const PAGE_SIZE = 50;

const ThesisPayamLib = ({ branches, itemTypeId }) => {
  // Stateها
  const [displayData, setDisplayData] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const loaderRef = useRef(null);
  const [isLoadingMoreUI, setIsLoadingMoreUI] = useState(false);
  const [filteredAllData, setFilteredAllData] = useState(null);
  const observerRef = useRef(null);
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false);

  // داده‌های اصلی از سرور
  const {
    data: allDataFromServer,
    isLoading: isLoadingFromServer,
    isLoadingMore: isLoadingMoreFromServer,
    totalCount,
  } = useThesis(branches, itemTypeId);


  // ردیابی وقتی داده‌های اولیه لود شدند
  useEffect(() => {
    if (
      allDataFromServer &&
      allDataFromServer.length > 0 &&
      !hasInitialDataLoaded
    ) {
      setHasInitialDataLoaded(true);
      // مطمئن شویم که visibleCount به درستی تنظیم شده
      const initialCount = Math.min(PAGE_SIZE, allDataFromServer.length);
      setVisibleCount(initialCount);
    }
  }, [allDataFromServer, hasInitialDataLoaded]);

  // داده‌های فعلی (فیلتر شده یا اصلی)
  const allData = useMemo(() => {
    const data =
      filteredAllData !== null ? filteredAllData : allDataFromServer || [];
    return data;
  }, [filteredAllData, allDataFromServer]);
  
  // به‌روزرسانی نمایش داده‌ها
  useEffect(() => {
    if (!allData || allData.length === 0) {
      setDisplayData([]);
      return;
    }

    const dataToShow = allData.slice(0, visibleCount);
    setDisplayData(dataToShow);
  }, [allData, visibleCount]);

  // استخراج options برای فیلترها
  const filterOptions = useMemo(() => {
    if (!allDataFromServer || allDataFromServer.length === 0) return null;

    const normalizeValue = (value) => {
      if (value == null) return "";
      return String(value).trim();
    };

    const extractValuesFromField = (field) => {
      const allValues = new Set();

      allDataFromServer.forEach((item) => {
        const value = item[field];

        if (Array.isArray(value)) {
          value.forEach((v) => {
            const normalized = normalizeValue(v);
            if (normalized) {
              allValues.add(normalized);
            }
          });
        } else {
          const normalized = normalizeValue(value);
          if (normalized) {
            allValues.add(normalized);
          }
        }
      });

      return Array.from(allValues);
    };

    const sortPersian = (array) => {
      if (!array || array.length <= 1) return array;

      return [...array].sort((a, b) => {
        const cleanA = a.replace(/^(دکتر|دكتر|مهندس|استاد)\s*/i, "");
        const cleanB = b.replace(/^(دکتر|دكتر|مهندس|استاد)\s*/i, "");
        return cleanA.localeCompare(cleanB, "fa");
      });
    };

    const majorsSet = new Set();

    allDataFromServer.forEach((item) => {
      const major = normalizeValue(item.major);
      const branch = normalizeValue(item.branch);

      if (major) {
        const value = branch ? `${branch} - ${major}` : major;
        majorsSet.add(value);
      } else if (branch) {
        majorsSet.add(branch);
      }
    });

    const authors = sortPersian(extractValuesFromField("authorStatement"));
    const degrees = sortPersian(extractValuesFromField("degree"));
    const majors = sortPersian(Array.from(majorsSet));
    const consultors = sortPersian(extractValuesFromField("consultor"));
    const supervisors = sortPersian(extractValuesFromField("supervisor"));

    return {
      authors: authors.map((value) => ({ value, label: value })),
      degrees: degrees.map((value) => ({ value, label: value })),
      majors: majors.map((value) => ({ value, label: value })),
      consultors: consultors.map((value) => ({ value, label: value })),
      supervisors: supervisors.map((value) => ({ value, label: value })),
    };
  }, [allDataFromServer]);

  // تابع لود بیشتر
  const loadMoreInUI = useCallback(() => {
    if (isLoadingMoreUI || !allData || allData.length === 0) return;

    const hasMore = visibleCount < allData.length;

    if (!hasMore) return;

    setIsLoadingMoreUI(true);

    // استفاده از setTimeout برای شبیه‌سازی لود (می‌توانید حذف کنید اگر نیاز نیست)
    setTimeout(() => {
      const newCount = Math.min(visibleCount + PAGE_SIZE, allData.length);
      setVisibleCount(newCount);
      setIsLoadingMoreUI(false);
    }, 300);
  }, [allData, visibleCount, isLoadingMoreUI]);

  // تنظیم Intersection Observer
  const setupObserver = useCallback(() => {
    // تمیز کردن observer قبلی
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // شرایط برای تنظیم observer جدید
    if (!loaderRef.current) return;
    if (!allData || allData.length === 0) return;
    if (visibleCount >= allData.length) return;
    if (isLoadingMoreUI) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMoreUI) {
          loadMoreInUI();
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    observerRef.current.observe(loaderRef.current);
  }, [allData, visibleCount, isLoadingMoreUI, loadMoreInUI]);

  // تنظیم observer زمانی که loaderRef رندر می‌شود یا داده‌ها تغییر می‌کنند
  useEffect(() => {
    // کمی تاخیر برای اطمینان از رندر DOM
    const timer = setTimeout(() => {
      if (hasInitialDataLoaded && loaderRef.current) {
        setupObserver();
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [hasInitialDataLoaded, setupObserver]);

  // وقتی فیلتر اعمال می‌شود
  const handleFilteredDataChange = useCallback(
    (newFilteredData) => {
      const filteredData = newFilteredData || [];
      setFilteredAllData(filteredData);
      setVisibleCount(Math.min(PAGE_SIZE, filteredData.length));
      setIsLoadingMoreUI(false);

      // بازنشانی observer بعد از اعمال فیلتر
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // کمی تاخیر برای اطمینان از رندر DOM
      setTimeout(() => {
        setupObserver();
      }, 150);
    },
    [setupObserver]
  );

  // محاسبه اطلاعات نمایش
  const showingCount = displayData.length;
  const totalItems = allData.length;
  const hasMoreItems = visibleCount < totalItems && totalItems > 0;
  const isFilterActive = filteredAllData !== null;

  // نمایش لودینگ سرور
  if (isLoadingFromServer) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-min-h-[400px]">
        <Loading />
      </div>
    );
  }

  return (
    <div className="tw-container tw-mx-auto tw-p-4">
      {/* فیلترها */}
      {allDataFromServer && allDataFromServer.length > 0 && (
        <FilterFormPayamLib
          filteredData={displayData}
          setFilteredData={handleFilteredDataChange}
          filterOptions={filterOptions}
          originalData={allDataFromServer}
          itemTypeId={itemTypeId}
        />
      )}

      {/* اطلاعات نمایش */}
      {displayData.length > 0 && (
        <div className="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded tw-p-4 tw-mb-6">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div>
              <span className="tw-font-medium">نمایش: </span>
              <span className="tw-text-blue-600 tw-font-medium">
                {showingCount} پایان‌نامه
              </span>
              {showingCount < totalItems && (
                <span className="tw-text-gray-600 tw-text-sm tw-mr-2">
                  (از {totalItems} پایان‌نامه)
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* لیست پایان‌نامه‌ها */}
      {displayData.length > 0 ? (
        <>
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-[30px]">
            {displayData.map((v, k) => (
              <ThesItemPayamLib key={`thesis-${k}-${v.id || k}`} {...v} />
            ))}
          </div>

          {/* Loader برای Infinite Scroll */}
          {hasMoreItems && !isLoadingMoreUI && (
            <div
              ref={loaderRef}
              key={`loader-${visibleCount}-${totalItems}`} // کلید برای رندر مجدد
              className="tw-mt-8 tw-mb-8 tw-p-6 tw-text-center tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg"
            >
              <div className="tw-text-gray-700 tw-mb-2">
                <span className="tw-font-bold">{visibleCount}</span> از{" "}
                <span className="tw-font-bold">{totalItems}</span> پایان‌نامه
                {isFilterActive && " (فیلتر شده)"}
              </div>
            </div>
          )}

          {/* نمایش وضعیت لودینگ UI */}
          {isLoadingMoreUI && (
            <div className="tw-mt-8 tw-mb-8 tw-p-6 tw-text-center">
              <p className="tw-text-gray-500 tw-mt-2">
                در حال بارگذاری اطلاعات بیشتر...
              </p>
            </div>
          )}

          {/* پیام پایان */}
          {/* {!hasMoreItems && totalItems > 0 && (
            <div className="tw-mt-8 tw-p-6 tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-text-center">
              <div className="tw-text-green-600 tw-text-xl tw-mb-2">✅</div>
              <p className="tw-text-green-800 tw-font-medium">
                تمام {totalItems} پایان‌نامه نمایش داده شد
              </p>
              <p className="tw-text-green-700 tw-text-sm tw-mt-1">
                {isFilterActive
                  ? "برای دیدن نتایج بیشتر، فیلترها را تغییر دهید"
                  : "برای جستجوی دقیق‌تر از فیلترهای بالا استفاده کنید"}
              </p>
            </div>
          )} */}
        </>
      ) : !isLoadingFromServer && allDataFromServer ? (
        <div className="tw-text-center tw-py-12 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200">
          <div className="tw-text-gray-400 tw-text-4xl tw-mb-4">📭</div>
          <p className="tw-text-gray-600">
            {isFilterActive
              ? "هیچ پایان‌نامه‌ای با این فیلترها یافت نشد"
              : "هیچ پایان‌نامه‌ای یافت نشد"}
          </p>
          {/* {isFilterActive && (
            <button
              onClick={() => handleFilteredDataChange(null)}
              className="tw-mt-4 tw-px-4 tw-py-2 tw-bg-blue-500 tw-text-white tw-rounded hover:tw-bg-blue-600 tw-transition"
            >
              حذف فیلترها
            </button>
          )} */}
        </div>
      ) : null}
    </div>
  );
};

export default ThesisPayamLib;
